import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid2 as Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  AttachMoney,
  CheckCircle,
  Schedule,
  Download,
  Visibility,
  Receipt,
  TrendingUp,
  Refresh,
  ArrowBack,
} from "@mui/icons-material";
// DatePicker temporarily removed due to dependency issues
// import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useSelector, useDispatch } from "react-redux";
import {
  loadPayouts,
  markPayoutPaid,
  loadFinanceSummary,
} from "../../../../store/actions/ppActions";
import { useNavigate } from "react-router";

const PpPayoutsPage = () => {
  const dispatch = useDispatch();
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const ppState = useSelector((state) => state[currentSession]?.pp);
  const payouts = ppState?.finance?.payouts || [];
  const financeSummary = ppState?.finance?.summary || {};
  const loading = ppState?.finance?.loading || false;

  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(loadPayouts());
    dispatch(loadFinanceSummary());
  }, [dispatch]);

  const handleMarkPaid = async () => {
    if (!selectedPayout) return;

    try {
      await dispatch(markPayoutPaid(selectedPayout.id));
      setMarkPaidDialogOpen(false);
      setSelectedPayout(null);
    } catch (error) {
      console.error("Failed to mark payout as paid:", error);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      paid: { color: "success", label: "Paid" },
      pending: { color: "warning", label: "Pending" },
      processing: { color: "info", label: "Processing" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredPayouts = payouts.filter((payout) => {
    if (statusFilter && payout.status !== statusFilter) return false;

    if (dateRange.start || dateRange.end) {
      const payoutDate = new Date(payout.month + "-01");
      if (dateRange.start && payoutDate < dateRange.start) return false;
      if (dateRange.end && payoutDate > dateRange.end) return false;
    }

    return true;
  });

  const totalPending = payouts
    .filter((p) => p.status === "pending")
    .reduce((sum, p) => sum + p.ppShare, 0);

  const totalPaid = payouts
    .filter((p) => p.status === "paid")
    .reduce((sum, p) => sum + p.ppShare, 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/pp/finance")}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography color="primary" variant="h4">
            Payouts
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            dispatch(loadPayouts());
            dispatch(loadFinanceSummary());
          }}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Monthly Earnings
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {formatCurrency(financeSummary.monthlyEarnings || 0)}
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: "primary.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Pending Payouts
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {formatCurrency(totalPending)}
                  </Typography>
                </Box>
                <Schedule sx={{ fontSize: 40, color: "warning.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Total Paid
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(totalPaid)}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: "success.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography variant="h6" color="text.secondary">
                    Avg Monthly
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {formatCurrency(financeSummary.avgMonthlyRevenue || 0)}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: "info.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={
                  dateRange.start
                    ? dateRange.start.toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    start: e.target.value ? new Date(e.target.value) : null,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={
                  dateRange.end ? dateRange.end.toISOString().split("T")[0] : ""
                }
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    end: e.target.value ? new Date(e.target.value) : null,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setStatusFilter("");
                  setDateRange({ start: null, end: null });
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payouts Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Total Revenue</TableCell>
                <TableCell>PP Share</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Paid Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading payouts...
                  </TableCell>
                </TableRow>
              ) : filteredPayouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No payouts found
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayouts.map((payout) => (
                  <TableRow key={payout.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {new Date(payout.month + "-01").toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "long",
                          }
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {payout.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(payout.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                      >
                        {formatCurrency(payout.ppShare)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        30% of total
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(payout.status)}</TableCell>
                    <TableCell>
                      {payout.paidDate ? formatDate(payout.paidDate) : "-"}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedPayout(payout);
                              setDetailsDialogOpen(true);
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {payout.status === "pending" && (
                          <Tooltip title="Mark as Paid">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setSelectedPayout(payout);
                                setMarkPaidDialogOpen(true);
                              }}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Download Statement">
                          <IconButton size="small" color="primary">
                            <Download />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Payout Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Receipt />
            Payout Details - {selectedPayout?.id}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedPayout && (
            <Box>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Period
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(selectedPayout.month + "-01").toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                      }
                    )}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusChip(selectedPayout.status)}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Revenue
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(selectedPayout.totalAmount)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Your Share (30%)
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(selectedPayout.ppShare)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Service Breakdown
              </Typography>
              <List>
                {selectedPayout.services?.map((service, index) => (
                  <ListItem key={index} divider>
                    <ListItemText
                      primary={service.type}
                      secondary={`${service.count} services`}
                    />
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(service.amount)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<Download />}>
            Download Statement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog
        open={markPaidDialogOpen}
        onClose={() => setMarkPaidDialogOpen(false)}
      >
        <DialogTitle>Mark Payout as Paid</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Mark payout {selectedPayout?.id} as paid?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Amount: {formatCurrency(selectedPayout?.ppShare || 0)}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            This action will update the payout status to "Paid" and record the
            current date as the payment date.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleMarkPaid}>
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpPayoutsPage;
